import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Heart } from 'lucide-react';
import { defaultOurStoryContent } from '../../data/pageContentDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Prefix API-served photos with BACKEND_URL; leave frontend-static paths and absolute URLs untouched.
const resolvePhoto = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const OurStory = () => {
  const [c, setC] = useState(defaultOurStoryContent);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/our-story/content`, { params: { _: Date.now() } });
        if (!alive || !res.data) return;
        setC({ ...defaultOurStoryContent, ...res.data });
      } catch { /* keep defaults */ }
    };
    fetchContent();
    const interval = setInterval(fetchContent, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return (
    <section id="story" className="section bg-cream" data-testid="story-section">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">{c.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight">
            {c.title}
          </h2>
          <div className="gold-divider" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {c.photoUrl && c.photoUrl.startsWith('/api/') ? (
                <img
                  src={resolvePhoto(c.photoUrl)}
                  alt="Mary and Arrington"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  data-testid="our-story-photo"
                />
              ) : (
                <picture>
                  <source srcSet={c.photoUrl || '/images/our-story-hands.webp'} type="image/webp" />
                  <img
                    src="/images/our-story-hands.jpg"
                    alt="Mary and Arrington holding hands"
                    className="w-full h-[500px] object-cover"
                    loading="lazy"
                    data-testid="our-story-photo"
                  />
                </picture>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#3B2F2F]/30 to-transparent" />
            </div>
            {/* Decorative frame */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#C8A951] rounded-2xl -z-10" />
          </motion.div>

          {/* Story Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-[#C8A951]" fill="#C8A951" />
              <span className="font-serif text-[#3B2F2F] text-lg">{c.imageBadge}</span>
            </div>

            {(c.paragraphs || []).map((p, i) => (
              <p key={i} className="text-[#5A4A4A] leading-relaxed text-base lg:text-lg whitespace-pre-wrap">
                {p}
              </p>
            ))}

            {c.quote && (
              <p className="text-[#5A4A4A] leading-relaxed text-base lg:text-lg italic border-l-4 border-[#C8A951] pl-4 whitespace-pre-wrap">
                &ldquo;{c.quote}&rdquo;
              </p>
            )}

            {c.signature && (
              <div className="pt-4">
                <p className="font-cursive text-[#C8A951] text-xl">{c.signature}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export { OurStory };
